/**
 * 認証ゲート。
 *
 * 起動時に一度だけ `/api/auth/session` を叩き、未認証ならログイン導線だけを出す。
 * ここで自動リダイレクトしないのは、リダイレクトループになったときに原因が
 * 見えなくなるため。ログインは明示的な操作で始める。
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '@app/api/client';
import type { SessionInfo } from '@shared/types';

type State =
  | { kind: 'checking' }
  | { kind: 'authenticated'; session: SessionInfo }
  | { kind: 'anonymous' }
  | { kind: 'error'; message: string };

/**
 * プレビュー限定のトークンログイン。
 *
 * プレビュー URL はバージョンごとに変わるため Google OAuth のリダイレクト URI を
 * 登録できない。その回避策（ADR-029）。**本番では表示されず、サーバー側でも 403 になる。**
 */
function PreviewLogin({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      className="preview-login"
      onSubmit={(ev) => {
        ev.preventDefault();
        setBusy(true);
        setError(null);
        api
          .previewLogin(token)
          .then(() => onDone())
          .catch((err: unknown) => {
            setError(err instanceof Error ? err.message : 'ログインできませんでした');
          })
          .finally(() => setBusy(false));
      }}
    >
      <p className="muted">
        ここはプレビュー環境です。本番の日記データには影響しません。
        <br />
        トークンでログインできます。
      </p>
      <input
        className="al-input"
        type="password"
        value={token}
        onChange={(ev) => setToken(ev.target.value)}
        placeholder="プレビュー用トークン"
        autoComplete="off"
        aria-label="プレビュー用トークン"
      />
      <button
        type="submit"
        className="al-button al-button--secondary"
        disabled={busy || token === ''}
      >
        {busy ? '確認中…' : 'トークンでログイン'}
      </button>
      {error && <p className="al-notice al-notice--danger">{error}</p>}
    </form>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ kind: 'checking' });
  const [environment, setEnvironment] = useState<'production' | 'preview' | null>(null);

  // 実行環境は認証前に分かる。プレビューでのみトークンログインの入口を出すため。
  useEffect(() => {
    const controller = new AbortController();
    api
      .getMeta(controller.signal)
      .then((meta) => setEnvironment(meta.environment))
      .catch(() => setEnvironment(null));
    return () => controller.abort();
  }, []);

  const check = useCallback(() => {
    const controller = new AbortController();
    setState({ kind: 'checking' });
    api
      .getSession(controller.signal)
      .then((session) => setState({ kind: 'authenticated', session }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof ApiError && error.isUnauthorized) {
          setState({ kind: 'anonymous' });
        } else {
          const message = error instanceof Error ? error.message : '不明なエラー';
          setState({ kind: 'error', message });
        }
      });
    return () => controller.abort();
  }, []);

  useEffect(() => check(), [check]);

  if (state.kind === 'checking') {
    return <div className="centered-message">読み込み中…</div>;
  }

  if (state.kind === 'error') {
    return (
      <div className="centered-message">
        <p>サーバーに接続できませんでした。</p>
        <p className="muted">{state.message}</p>
        <button type="button" className="al-button al-button--secondary" onClick={check}>
          再試行
        </button>
      </div>
    );
  }

  if (state.kind === 'anonymous') {
    return (
      <div className="centered-message">
        <h1 className="brand-mark">mamemo</h1>
        <p className="muted">Google アカウントでログインしてください。</p>
        <a className="al-button al-button--primary" href={api.loginUrl()}>
          Google でログイン
        </a>
        {/* プレビューでは Google のリダイレクト URI を登録できないため、
            トークンでの入口を出す（ADR-029）。本番では environment が
            'production' になるので表示されない。 */}
        {environment === 'preview' && <PreviewLogin onDone={check} />}
      </div>
    );
  }

  return <>{children}</>;
}
